import React, { useState, useMemo } from 'react';
import SubTabs from '../../layout/SubTabs';
import DimensionHeader from '../../common/DimensionHeader';
import { useTheme } from '../../../utils/ThemeSwitcher';
import { AnimatedNumber, AnimatedCard } from '../../common/AnimationUtils';
import {
  MessageSquare,
  Users,
  AlertTriangle,
  CheckCircle,
  ChevronDown,
  ChevronUp,
  Copy,
  Search,
  Layers,
  TrendingDown,
  Target,
  Zap,
  BarChart3,
  ArrowRight,
  Info,
  XCircle,
  Filter,
  Type,  // NEW: for gibberish icon
  Table,  // For collector trends toggle
  ClipboardList
} from 'lucide-react';
import DetailedIssueTooltip from './DetailedIssueTooltip';  // NEW: Hover tooltip for row details
import IssueLogPanel from './IssueLogPanel';  // Inline issue log tab
import PulseBeacon from './PulseBeacon';  // Animated issue indicator
// TrendSummaryBanner removed — text quality scores don't trend meaningfully
import CollapsibleSection from './CollapsibleSection';
import ScoreCard from '../charts/ScoreCard';
import TextDiversityHeatmap from '../charts/TextDiversityHeatmap';
import EmptyStateCelebration from './EmptyStateCelebration';  // NEW: Time-series trend visualization
// CollectorTrendChart removed — text quality trends not applicable

// Hoisted to module scope: defining this inside the section component gave it a new
// identity on every re-render, which remounted every card and restarted the load animation.
const MetricCard = ({ colors, isDarkMode, onViewIssues, icon: Icon, title, value, subtitle, color, detailedIssues = [], issueType = 'text', totalCount = null, severity, animDelay = 0 }) => {
  const sevColor = severity === 'critical' ? '#b91c1c' : severity === 'warning' ? '#b45309' : null;
  const sevColorDark = severity === 'critical' ? '#f87171' : severity === 'warning' ? '#fbbf24' : null;
  const iconColor = isDarkMode ? (sevColorDark || '#60a5fa') : (sevColor || '#2563EB');
  const hasIssues = detailedIssues.length > 0;

  const cardContent = (
    <div
      className="nsx-metric-card"
      style={{
        position: 'relative',
        padding: '18px',
        backgroundColor: isDarkMode ? 'rgba(255,255,255,0.03)' : 'rgba(255,255,255,0.6)',
        borderRadius: '8px',
        border: `0.5px solid ${isDarkMode ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.08)'}`,
        backdropFilter: 'blur(20px)',
        WebkitBackdropFilter: 'blur(20px)',
        transition: 'transform 0.25s cubic-bezier(0.4, 0, 0.2, 1), box-shadow 0.25s ease',
        overflow: 'hidden',
        cursor: hasIssues ? 'pointer' : 'default',
        fontFamily: "'Outfit', -apple-system, sans-serif",
      }}
    >
      {severity && (
        <svg
          width="30" height="30" viewBox="0 0 30 30"
          style={{ position: 'absolute', top: 0, left: 0, pointerEvents: 'none', overflow: 'visible' }}
        >
          <path
            d="M 28 0 L 8 0 A 8 8 0 0 0 0 8 L 0 28"
            stroke={iconColor} strokeWidth="2" fill="none"
            strokeLinecap="round" opacity={0.9}
          />
        </svg>
      )}

      <PulseBeacon severity={severity} isDarkMode={isDarkMode} />

      <div style={{ display: 'flex', alignItems: 'center', gap: '14px' }}>
        <div style={{
          width: '36px',
          height: '36px',
          borderRadius: '5px',
          background: severity
            ? (isDarkMode
                ? `linear-gradient(135deg, ${iconColor}22, ${iconColor}11)`
                : `linear-gradient(135deg, ${iconColor}18, ${iconColor}08)`)
            : 'linear-gradient(135deg, rgba(37,99,235,0.14), rgba(217,119,6,0.12))',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          flexShrink: 0,
          color: iconColor,
        }}>
          <Icon size={16} strokeWidth={1.75} />
        </div>
        <div style={{ flex: 1, minWidth: 0, textAlign: 'center' }}>
          <div style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: '6px',
            fontSize: '10px',
            fontWeight: 500,
            letterSpacing: '0.16em',
            textTransform: 'lowercase',
            color: colors.textMuted,
            marginBottom: '10px',
          }}>
            {title}
            {hasIssues && (
              <Info size={11} style={{ color: '#2563EB', strokeWidth: 2 }} />
            )}
          </div>
          <div
            className="metric-value"
            style={{
              fontSize: '32px',
              fontWeight: 500,
              letterSpacing: '-0.02em',
              fontVariantNumeric: 'tabular-nums',
              color: colors.text,
              lineHeight: 1.05,
              marginBottom: '6px',
            }}
          >
            <AnimatedNumber value={value} duration={800} />
          </div>
          {(
            <div style={{
              fontSize: '11.5px',
              fontWeight: 400,
              letterSpacing: '-0.005em',
              color: hasIssues ? '#2563EB' : colors.textMuted,
              lineHeight: 1.4,
            }}>
              {hasIssues ? 'View issue log →' : (subtitle || '\u00A0')}
            </div>
          )}
        </div>
        <div style={{ width: '36px', flexShrink: 0 }} aria-hidden="true" />
      </div>
    </div>
  );

  if (hasIssues) {
    return (
      <AnimatedCard delay={animDelay} translateY={12}>
      <div onClick={() => onViewIssues && onViewIssues()}>
        {cardContent}
      </div>
      </AnimatedCard>
    );
  }

  return <AnimatedCard delay={animDelay} translateY={12}>{cardContent}</AnimatedCard>;
};

const TextAnalysisSection = ({ data, dataCollectorId, isSidePanelExpanded, isMobile = false }) => {
  const { isDarkMode } = useTheme();
  
  // State for active tab and expanded clusters
  const [activeTab, setActiveTab] = useState('overview');
  const [expandedClusters, setExpandedClusters] = useState({});
  const [selectedVariable, setSelectedVariable] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [collectorView, setCollectorView] = useState('heatmap');
  // collectorView toggle removed — only table view for text analysis
  
  // Color scheme
  const colors = {
    background: isDarkMode ? '#0D0E0E' : '#F5F5F5',
    cardBg: isDarkMode ? '#17171B' : '#FFFFFF',
    border: isDarkMode ? '#2D2E32' : '#E5E7EB',
    text: isDarkMode ? '#E5E7EB' : '#1F2937',
    textSecondary: isDarkMode ? '#9CA3AF' : '#6B7280',
    textMuted: isDarkMode ? '#6B7280' : '#9CA3AF',
    primary: '#3B82F6',
    success: '#10B981',
    warning: '#F59E0B',
    danger: '#EF4444',
    purple: '#8B5CF6',
    cyan: '#06B6D4',
    neutral: '#6B7280',
  };
  
  // Extract data - Note: data.overall contains the check result, which has its own 'overall' for stats
  const results = data?.overall || {};
  const overallStats = results.overall || {};  // The actual statistics
  const byVariable = results.by_variable || {};
  const byCollector = results.by_collector || {};
  const qualityIssues = results.quality_issues || [];
  const recommendations = results.recommendations || [];
  
  // Get list of analyzed variables, sorted by usability so the picker leads
  // with the variable that has the most measurable text. Without this the
  // picker defaults alphabetically and lands on whatever happens to come
  // first — often a low-text categorical column that produces 0 valid
  // responses, making the whole tab look broken.
  const analyzedVariables = Object.keys(byVariable);
  const sortedAnalyzedVariables = useMemo(() => {
    return [...analyzedVariables].sort((a, b) => {
      const countA = byVariable[a]?.valid_responses ?? 0;
      const countB = byVariable[b]?.valid_responses ?? 0;
      return countB - countA;
    });
  }, [analyzedVariables, byVariable]);

  // Auto-select the most useful variable on first render. Preference order:
  //   1. First variable with ≥5 valid responses (the clustering threshold)
  //   2. Variable with the highest valid count (even if < 5)
  //   3. First variable in sorted order (last-resort fallback)
  React.useEffect(() => {
    if (!selectedVariable && sortedAnalyzedVariables.length > 0) {
      const firstClusterable = sortedAnalyzedVariables.find(
        v => (byVariable[v]?.valid_responses ?? 0) >= 5
      );
      setSelectedVariable(firstClusterable || sortedAnalyzedVariables[0]);
    }
  }, [sortedAnalyzedVariables, selectedVariable, byVariable]);
  
  // Current variable data
  const currentVarData = selectedVariable ? byVariable[selectedVariable] : null;
  
  // NEW: Aggregate all similar pairs across variables for tooltip
  const allSimilarPairs = useMemo(() => {
    const pairs = [];
    Object.entries(byVariable).forEach(([variable, varData]) => {
      if (varData?.similar_pairs?.length > 0) {
        varData.similar_pairs.forEach(pair => {
          // Convert to detailed issue format
          pairs.push({
            row_index: pair.response_1?.row_index,
            variable: variable,
            value: `${pair.similarity * 100}% match`,
            respondent_id: pair.response_1?.respondent_id,
            collector_id: pair.response_1?.collector_id,
            interview_date: pair.response_1?.interview_date,
            issue_type: 'similar_response',
            // Include second response info
            paired_row_index: pair.response_2?.row_index,
            paired_respondent_id: pair.response_2?.respondent_id,
            text_1: pair.response_1?.text,
            text_2: pair.response_2?.text
          });
        });
      }
    });
    return pairs.slice(0, 100);
  }, [byVariable]);
  
  // NEW: Aggregate gibberish issues across variables for tooltip
  const allGibberishIssues = useMemo(() => {
    const issues = [];
    Object.entries(byVariable).forEach(([variable, varData]) => {
      // Check for detailed gibberish issues (new format)
      const qualityIndicators = varData?.quality_indicators;
      if (qualityIndicators?.gibberish_detailed_issues?.length > 0) {
        issues.push(...qualityIndicators.gibberish_detailed_issues);
      } else if (qualityIndicators?.gibberish_examples?.length > 0) {
        // Fallback to examples (old format)
        qualityIndicators.gibberish_examples.forEach(example => {
          issues.push({
            row_index: example.row_index,
            variable: variable,
            value: example.text,
            respondent_id: null,
            collector_id: null,
            interview_date: null,
            issue_type: 'gibberish',
            reason: example.reason
          });
        });
      }
    });
    return issues.slice(0, 100);
  }, [byVariable]);
  
  // Calculate total gibberish count
  const totalGibberishCount = useMemo(() => {
    return Object.values(byVariable).reduce((sum, varData) => {
      return sum + (varData?.quality_indicators?.gibberish_count || 0);
    }, 0);
  }, [byVariable]);
  
  const hasTextIssues = allSimilarPairs.length > 0 || allGibberishIssues.length > 0;
  
  // Tab configuration
  const tabs = [
    { id: 'overview', label: 'Overview', icon: BarChart3 },
    { id: 'clusters', label: 'Themes & Clusters', icon: Layers },
    { id: 'similar', label: 'Similar Responses', icon: Copy },
    { id: 'outliers', label: 'Outliers', icon: AlertTriangle },
    ...(Object.keys(byCollector).length > 0 ? [{ id: 'collectors', label: 'By Collector', icon: Users }] : []),
    ...(hasTextIssues ? [{ id: 'issues', label: 'Issue Log', icon: ClipboardList }] : []),
  ];
  
  // Severity helpers
  const getSeverityColor = (severity) => {
    switch (severity) {
      case 'critical': return colors.danger;
      case 'warning': return colors.warning;
      case 'info': return colors.primary;
      default: return colors.success;
    }
  };
  
  const getScoreColor = (score) => {
    if (score >= 80) return colors.success;
    if (score >= 60) return colors.warning;
    return colors.danger;
  };
  
  // ============== COMPONENTS ==============
  
  
  
  const ClusterCard = ({ cluster, variableName, isExpanded, onToggle }) => {
    const topTerms = cluster.top_terms || [];
    const samples = cluster.sample_responses || [];
    
    return (
      <div className="nsx-metric-card" style={{
        backgroundColor: colors.cardBg,
        borderRadius: '12px',
        border: `1px solid ${colors.border}`,
        overflow: 'hidden',
        marginBottom: '12px'
      }}>
        <div 
          onClick={onToggle}
          style={{
            padding: '12px 16px',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            backgroundColor: isDarkMode ? '#1F1F23' : '#F9FAFB'
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            <div style={{
              width: '36px',
              height: '36px',
              borderRadius: '8px',
              backgroundColor: `${colors.primary}20`,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: colors.primary,
              fontWeight: '700',
              fontSize: '13px'
            }}>
              {cluster.cluster_id + 1}
            </div>
            <div>
              <div style={{ fontSize: '13px', fontWeight: '600', color: colors.text }}>
                {cluster.theme_label}
              </div>
              <div style={{ fontSize: '13px', color: colors.textSecondary }}>
                {cluster.size} responses ({cluster.percentage}%)
              </div>
            </div>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
            <div style={{
              display: 'flex',
              gap: '6px',
              flexWrap: 'wrap',
              maxWidth: '300px'
            }}>
              {topTerms.slice(0, 3).map((term, i) => (
                <span key={i} style={{
                  padding: '4px 10px',
                  backgroundColor: `${colors.primary}15`,
                  color: colors.primary,
                  borderRadius: '12px',
                  fontSize: '12px',
                  fontWeight: '500'
                }}>
                  {term}
                </span>
              ))}
            </div>
            {isExpanded ? <ChevronUp size={20} /> : <ChevronDown size={20} />}
          </div>
        </div>
        
        {isExpanded && samples.length > 0 && (
          <div style={{ padding: '12px 16px', borderTop: `1px solid ${colors.border}` }}>
            <div style={{ fontSize: '13px', fontWeight: '600', color: colors.textSecondary, marginBottom: '12px' }}>
              Sample Responses
            </div>
            {samples.map((sample, i) => (
              <div key={i} style={{
                padding: '12px 16px',
                backgroundColor: isDarkMode ? '#1F1F23' : '#F3F4F6',
                borderRadius: '8px',
                marginBottom: '8px',
                fontSize: '14px',
                color: colors.text,
                lineHeight: '1.5'
              }}>
                "{sample.text}"
                {sample.collector && (
                  <div style={{ fontSize: '12px', color: colors.textMuted, marginTop: '6px' }}>
                    Collector: {sample.collector} | Row: {sample.row_index}
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    );
  };
  
  const SimilarPairCard = ({ pair, index }) => (
    <div className="nsx-metric-card" style={{
      backgroundColor: colors.cardBg,
      borderRadius: '12px',
      border: `1px solid ${pair.similarity >= 0.95 ? colors.danger : colors.warning}40`,
      borderLeft: `0px solid ${pair.similarity >= 0.95 ? colors.danger : colors.warning}`,
      padding: '12px 16px',
      marginBottom: '12px'
    }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
        <div style={{
          display: 'flex',
          alignItems: 'center',
          gap: '8px'
        }}>
          <Copy size={16} style={{ color: pair.similarity >= 0.95 ? colors.danger : colors.warning }} />
          <span style={{ fontSize: '14px', fontWeight: '600', color: colors.text }}>
            Similarity: {(pair.similarity * 100).toFixed(1)}%
          </span>
        </div>
        {pair.same_collector !== undefined && (
          <span style={{
            padding: '4px 10px',
            backgroundColor: pair.same_collector ? `${colors.danger}15` : `${colors.success}15`,
            color: pair.same_collector ? colors.danger : colors.success,
            borderRadius: '12px',
            fontSize: '12px',
            fontWeight: '500'
          }}>
            {pair.same_collector ? 'Same Collector' : 'Different Collectors'}
          </span>
        )}
      </div>
      
      <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', gap: '16px' }}>
        <div style={{
          padding: '12px',
          backgroundColor: isDarkMode ? '#1F1F23' : '#F9FAFB',
          borderRadius: '8px'
        }}>
          <div style={{ fontSize: '12px', color: colors.textMuted, marginBottom: '6px' }}>
            Response 1 {pair.response_1?.collector && `• ${pair.response_1.collector}`}
          </div>
          <div style={{ fontSize: '14px', color: colors.text, lineHeight: '1.5' }}>
            "{pair.response_1?.text}"
          </div>
        </div>
        <div style={{
          padding: '12px',
          backgroundColor: isDarkMode ? '#1F1F23' : '#F9FAFB',
          borderRadius: '8px'
        }}>
          <div style={{ fontSize: '12px', color: colors.textMuted, marginBottom: '6px' }}>
            Response 2 {pair.response_2?.collector && `• ${pair.response_2.collector}`}
          </div>
          <div style={{ fontSize: '14px', color: colors.text, lineHeight: '1.5' }}>
            "{pair.response_2?.text}"
          </div>
        </div>
      </div>
    </div>
  );
  
  
  const VariableSelector = () => (
    <div className="nsx-metric-card" style={{
      display: 'flex',
      alignItems: 'center',
      gap: '12px',
      marginBottom: '24px',
      padding: '12px 16px',
      backgroundColor: colors.cardBg,
      borderRadius: '12px',
      border: `1px solid ${colors.border}`
    }}>
      <Filter size={18} style={{ color: colors.textSecondary }} />
      <span style={{ fontSize: '13px', color: colors.textSecondary }}>Variable:</span>
      <select
        value={selectedVariable || ''}
        onChange={(e) => setSelectedVariable(e.target.value)}
        style={{
          padding: '8px 16px',
          borderRadius: '8px',
          border: `1px solid ${colors.border}`,
          backgroundColor: isDarkMode ? '#1F1F23' : '#F9FAFB',
          color: colors.text,
          fontSize: '14px',
          fontWeight: '500',
          cursor: 'pointer',
          minWidth: '200px'
        }}
      >
        {sortedAnalyzedVariables.map(v => {
          const validCount = byVariable[v]?.valid_responses ?? 0;
          const suffix = validCount === 0
            ? ' — no usable text'
            : validCount < 5
              ? ` (${validCount} valid · too few to cluster)`
              : ` (${validCount} valid)`;
          return <option key={v} value={v}>{v}{suffix}</option>;
        })}
      </select>
      {currentVarData && (
        <span style={{ fontSize: '13px', color: colors.textMuted, marginLeft: 'auto' }}>
          {currentVarData.valid_responses} valid responses analyzed
        </span>
      )}
    </div>
  );
  
  // ============== TAB RENDERS ==============
  
  const renderOverviewTab = () => (
    <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0, 1fr)', gap: '16px', marginTop: '80px', width: '100%' }}>

      <CollapsibleSection 
        title="Key Metrics" 
        isDarkMode={isDarkMode} 
        colors={colors}
        badge={
          (overallStats.total_similar_pairs > 5 || totalGibberishCount > 0) ? (
            <span style={{ padding: '2px 8px', borderRadius: '10px', backgroundColor: '#EF444420', color: '#EF4444', fontSize: '11px', fontWeight: '600' }}>
              {(overallStats.total_similar_pairs || 0) + totalGibberishCount} issues
            </span>
          ) : null
        }
      >
      {/* Summary Stats */}
      <div style={{ display: 'grid', gridTemplateColumns: isMobile ? 'repeat(2, minmax(0, 1fr))' : 'repeat(auto-fit, minmax(min(200px, 100%), 1fr))', gap: '16px' }}>
        <MetricCard
          colors={colors}
          isDarkMode={isDarkMode}
          onViewIssues={() => setActiveTab('issues')}
          animDelay={0}
          icon={MessageSquare}
          title="Variables Analyzed"
          value={overallStats.total_variables_analyzed || 0}
          subtitle="Text fields processed"
          color={colors.neutral}
        />
        <MetricCard
          colors={colors}
          isDarkMode={isDarkMode}
          onViewIssues={() => setActiveTab('issues')}
          animDelay={80}
          icon={Target}
          title="Total Responses"
          value={(overallStats.total_responses_analyzed || 0).toLocaleString()}
          subtitle="Valid text responses"
          color={colors.neutral}
        />
        <MetricCard
          colors={colors}
          isDarkMode={isDarkMode}
          onViewIssues={() => setActiveTab('issues')}
          animDelay={160}
          icon={Layers}
          title="Themes Found"
          value={overallStats.total_clusters_found || 0}
          subtitle="Across all variables"
          color={colors.neutral}
        />
        <MetricCard
          colors={colors}
          isDarkMode={isDarkMode}
          onViewIssues={() => setActiveTab('issues')}
          animDelay={240}
          icon={Copy}
          title="Similar Pairs"
          value={overallStats.total_similar_pairs || 0}
          subtitle={overallStats.total_similar_pairs > 5 ? 'Needs review' : 'Within normal range'}
          color={overallStats.total_similar_pairs > 5 ? colors.warning : colors.neutral}
          detailedIssues={allSimilarPairs}
          issueType="similar"
          totalCount={overallStats.total_similar_pairs}
          severity={overallStats.total_similar_pairs > 20 ? 'critical' : overallStats.total_similar_pairs > 5 ? 'warning' : null}
        />
        {totalGibberishCount > 0 && (
          <MetricCard
            colors={colors}
            isDarkMode={isDarkMode}
            onViewIssues={() => setActiveTab('issues')}
            animDelay={320}
            icon={Type}
            title="Gibberish Detected"
            value={totalGibberishCount}
            subtitle="Potential keyboard mashing"
            color={colors.danger}
            detailedIssues={allGibberishIssues}
            issueType="gibberish"
            totalCount={totalGibberishCount}
            severity={totalGibberishCount > 10 ? 'critical' : 'warning'}
          />
        )}
      </div>
      </CollapsibleSection>
      
      <CollapsibleSection title="Analysis & Issues" isDarkMode={isDarkMode} colors={colors}>
      {/* Quality Score & Recommendations (equal heights via .analysis-grid-4col) */}
      <div className="analysis-grid-4col ta-analysis-grid" style={{ display: 'grid', gridTemplateColumns: isMobile ? 'minmax(0, 1fr)' : 'minmax(0, 1fr) minmax(0, 3fr)', gap: '16px', marginBottom: '16px' }}>
        {/* Quality Score */}
        <AnimatedCard delay={320} translateY={12}>
          {(() => {
            const q = overallStats.quality_score;
            const noData = q === null || q === undefined;
            return (
              <ScoreCard
                eyebrow="text quality score"
                score={noData ? null : q}
                label={noData ? '' : q >= 80 ? 'Good' : q >= 60 ? 'Moderate' : 'Poor'}
                caption={noData ? 'Not enough text to assess'
                  : q >= 80 ? 'Good response quality'
                  : q >= 60 ? 'Some issues detected'
                  : 'Significant issues found'}
                color={noData ? null : getScoreColor(q)}
                severity={noData ? null : q < 60 ? 'critical' : q < 80 ? 'warning' : null}
                isDarkMode={isDarkMode}
                startDelay={620}
              />
            );
          })()}
        </AnimatedCard>
        
        {/* Recommendations */}
        <AnimatedCard delay={400} translateY={12} style={{ display: 'flex' }}>
        <div className="nsx-metric-card" style={{
          padding: '18px',
          backgroundColor: colors.cardBg,
          borderRadius: '12px',
          border: `1px solid ${colors.border}`,
          flex: 1,
          display: 'flex',
          flexDirection: 'column'
        }}>
          <h3 style={{ fontSize: '14px', fontWeight: '600', color: colors.text, marginBottom: '12px' }}>
            Recommendations
          </h3>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '12px', flex: 1 }}>
            {recommendations.map((rec, i) => (
              <div key={i} style={{
                padding: '14px 18px',
                backgroundColor: rec.priority === 'high' 
                  ? (isDarkMode ? '#1F1116' : '#FEF2F2')
                  : rec.priority === 'medium'
                  ? (isDarkMode ? '#1C1917' : '#FFFBEB')
                  : (isDarkMode ? '#0F172A' : '#F0F9FF'),
                borderRadius: '10px',
                border: `1px solid ${
                  rec.priority === 'high' ? colors.danger : 
                  rec.priority === 'medium' ? colors.warning : colors.primary
                }30`
              }}>
                <div style={{ display: 'flex', alignItems: 'flex-start', gap: '12px' }}>
                  {rec.category === 'success' ? (
                    <CheckCircle size={18} style={{ color: colors.success, marginTop: '2px' }} />
                  ) : rec.priority === 'high' ? (
                    <AlertTriangle size={18} style={{ color: colors.danger, marginTop: '2px' }} />
                  ) : (
                    <Info size={18} style={{ color: colors.primary, marginTop: '2px' }} />
                  )}
                  <div>
                    <div style={{ 
                      fontSize: '14px', 
                      fontWeight: '600', 
                      color: colors.text,
                      marginBottom: '4px'
                    }}>
                      {rec.title}
                    </div>
                    <div style={{ fontSize: '13px', color: colors.textSecondary }}>
                      {rec.description}
                    </div>
                  </div>
                </div>
              </div>
            ))}
            {recommendations.length === 0 && (
              <div style={{ 
                padding: '16px', 
                textAlign: 'center', 
                color: colors.textMuted,
                fontSize: '13px'
              }}>
                No specific recommendations at this time.
              </div>
            )}
          </div>
        </div>
        </AnimatedCard>
      </div>
      
      <style>{`
        @media (max-width: 1200px) { .ta-analysis-grid { grid-template-columns: minmax(0, 1fr) minmax(0, 2fr) !important; } }
        @media (max-width: 768px) { .ta-analysis-grid { grid-template-columns: minmax(0, 1fr) !important; } }
      `}</style>

      {/* Quality Issues */}
      {qualityIssues.length > 0 && (
        <AnimatedCard delay={480} translateY={12}>
        <div className="nsx-metric-card" style={{
          padding: '18px',
          backgroundColor: colors.cardBg,
          borderRadius: '12px',
          border: `1px solid ${colors.border}`
        }}>
          <h3 style={{ fontSize: '14px', fontWeight: '600', color: colors.text, marginBottom: '12px' }}>
            Quality Issues Detected
          </h3>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
            {qualityIssues.map((issue, i) => {
              const sevColor = getSeverityColor(issue.severity);
              return (
              <div key={i} style={{
                position: 'relative',
                display: 'flex',
                alignItems: 'center',
                gap: '12px',
                padding: '12px 16px',
                backgroundColor: isDarkMode ? '#1F1F23' : '#F9FAFB',
                borderRadius: '8px'
              }}>
                {/* Severity corner bracket — SVG L-shape tracing the 8px rounded top-left corner */}
                <svg
                  width="30"
                  height="30"
                  viewBox="0 0 30 30"
                  style={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    pointerEvents: 'none',
                    overflow: 'visible',
                  }}
                >
                  <path
                    d="M 28 0 L 8 0 A 8 8 0 0 0 0 8 L 0 28"
                    stroke={sevColor}
                    strokeWidth="2"
                    fill="none"
                    strokeLinecap="round"
                    opacity={0.9}
                  />
                </svg>
                <div style={{
                  padding: '4px 10px',
                  backgroundColor: `${sevColor}20`,
                  color: sevColor,
                  borderRadius: '12px',
                  fontSize: '12px',
                  fontWeight: '600',
                  textTransform: 'capitalize'
                }}>
                  {issue.severity}
                </div>
                <div style={{ fontSize: '14px', color: colors.text, flex: 1 }}>
                  {issue.message}
                </div>
              </div>
              );
            })}
          </div>
        </div>
        </AnimatedCard>
      )}
      </CollapsibleSection>
    </div>
  );
  
  const renderClustersTab = () => {
    if (!currentVarData) return null;
    
    const clusters = currentVarData.clusters || [];
    const diversity = currentVarData.response_diversity || {};
    
    return (
      <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0, 1fr)', gap: '16px', marginTop: '80px', width: '100%' }}>
        <VariableSelector />
        
        {/* Diversity Stats */}
        <AnimatedCard delay={0} translateY={12}>
        <div style={{ display: 'grid', gridTemplateColumns: isMobile ? 'repeat(2, minmax(0, 1fr))' : 'repeat(auto-fit, minmax(min(180px, 100%), 1fr))', gap: '16px' }}>
          <MetricCard
            colors={colors}
            isDarkMode={isDarkMode}
            onViewIssues={() => setActiveTab('issues')}
            animDelay={0}
            icon={Layers}
            title="Themes Found"
            value={clusters.length}
            subtitle={clusters.length === 1 ? 'Cluster in this variable' : 'Clusters in this variable'}
            color={colors.neutral}
          />
          <MetricCard
            colors={colors}
            isDarkMode={isDarkMode}
            onViewIssues={() => setActiveTab('issues')}
            animDelay={80}
            icon={Target}
            title="Unique Ratio"
            value={`${((diversity.unique_response_ratio || 0) * 100).toFixed(1)}%`}
            subtitle={diversity.unique_responses + ' unique responses'}
            color={diversity.unique_response_ratio > 0.5 ? colors.success : colors.warning}
          />
          <MetricCard
            colors={colors}
            isDarkMode={isDarkMode}
            onViewIssues={() => setActiveTab('issues')}
            animDelay={160}
            icon={MessageSquare}
            title="Avg. Length"
            value={`${diversity.avg_word_count || 0} words`}
            subtitle={`Range: ${diversity.min_word_count || 0}-${diversity.max_word_count || 0}`}
            color={colors.neutral}
          />
          <MetricCard
            colors={colors}
            isDarkMode={isDarkMode}
            onViewIssues={() => setActiveTab('issues')}
            animDelay={240}
            icon={BarChart3}
            title="Vocabulary"
            value={(diversity.unique_words || 0).toLocaleString()}
            subtitle={`${((diversity.vocabulary_richness || 0) * 100).toFixed(1)}% richness`}
            color={colors.neutral}
          />
        </div>
        </AnimatedCard>
        
        {/* Top Words */}
        {diversity.top_words && diversity.top_words.length > 0 && (
          <div className="nsx-metric-card" style={{
            padding: '20px 24px',
            backgroundColor: colors.cardBg,
            borderRadius: '12px',
            border: `1px solid ${colors.border}`
          }}>
            <h4 style={{ fontSize: '13px', fontWeight: '600', color: colors.text, marginBottom: '12px' }}>
              Most Common Words
            </h4>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
              {diversity.top_words.slice(0, 15).map((word, i) => (
                <span key={i} style={{
                  padding: '6px 14px',
                  backgroundColor: isDarkMode ? '#1F1F23' : '#F3F4F6',
                  color: colors.text,
                  borderRadius: '16px',
                  fontSize: '13px',
                  fontWeight: '500'
                }}>
                  {word.word} <span style={{ color: colors.textMuted }}>({word.count})</span>
                </span>
              ))}
            </div>
          </div>
        )}
        
        {/* Clusters */}
        <AnimatedCard delay={80} translateY={12}>
        <div>
          <h3 style={{ fontSize: '14px', fontWeight: '600', color: colors.text, marginBottom: '12px' }}>
            Response Themes ({clusters.length})
          </h3>
          {clusters.map((cluster, i) => (
            <ClusterCard
              key={i}
              cluster={cluster}
              variableName={selectedVariable}
              isExpanded={expandedClusters[`${selectedVariable}-${cluster.cluster_id}`]}
              onToggle={() => setExpandedClusters(prev => ({
                ...prev,
                [`${selectedVariable}-${cluster.cluster_id}`]: !prev[`${selectedVariable}-${cluster.cluster_id}`]
              }))}
            />
          ))}
          {clusters.length === 0 && (
            <div className="nsx-metric-card" style={{ 
              padding: '40px', 
              textAlign: 'center', 
              color: colors.textMuted,
              backgroundColor: colors.cardBg,
              borderRadius: '12px',
              border: `1px solid ${colors.border}`
            }}>
              No clusters generated for this variable.
            </div>
          )}
        </div>
        </AnimatedCard>
      </div>
    );
  };
  
  const renderSimilarTab = () => {
    if (!currentVarData) return null;
    
    const similarPairs = currentVarData.similar_pairs || [];
    
    // Filter by search term
    const filteredPairs = searchTerm 
      ? similarPairs.filter(p => 
          p.response_1?.text?.toLowerCase().includes(searchTerm.toLowerCase()) ||
          p.response_2?.text?.toLowerCase().includes(searchTerm.toLowerCase())
        )
      : similarPairs;
    
    return (
      <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0, 1fr)', gap: '16px', marginTop: '80px', width: '100%' }}>
        <VariableSelector />
        
        {/* Summary */}
        <AnimatedCard delay={0} translateY={12}>
        <div style={{ display: 'grid', gridTemplateColumns: isMobile ? 'repeat(2, minmax(0, 1fr))' : 'repeat(auto-fit, minmax(min(200px, 100%), 1fr))', gap: '16px' }}>
          <MetricCard
            colors={colors}
            isDarkMode={isDarkMode}
            onViewIssues={() => setActiveTab('issues')}
            animDelay={0}
            icon={Copy}
            title="Similar Pairs Found"
            value={similarPairs.length}
            subtitle={similarPairs.length > 10 ? 'High - investigate' : 'Normal range'}
            color={similarPairs.length > 10 ? colors.danger : colors.success}
          />
          <MetricCard
            colors={colors}
            isDarkMode={isDarkMode}
            onViewIssues={() => setActiveTab('issues')}
            animDelay={80}
            icon={AlertTriangle}
            title="High Similarity (≥95%)"
            value={similarPairs.filter(p => p.similarity >= 0.95).length}
            subtitle="Potential copying"
            color={colors.danger}
          />
          <MetricCard
            colors={colors}
            isDarkMode={isDarkMode}
            onViewIssues={() => setActiveTab('issues')}
            animDelay={160}
            icon={Users}
            title="Same Collector"
            value={similarPairs.filter(p => p.same_collector).length}
            subtitle="From same interviewer"
            color={colors.warning}
          />
        </div>
        </AnimatedCard>
        
        {/* Search */}
        <AnimatedCard delay={80} translateY={12}>
        {similarPairs.length > 0 && (
          <div className="nsx-metric-card" style={{
            display: 'flex',
            alignItems: 'center',
            gap: '12px',
            padding: '12px 16px',
            backgroundColor: colors.cardBg,
            borderRadius: '10px',
            border: `1px solid ${colors.border}`
          }}>
            <Search size={18} style={{ color: colors.textMuted }} />
            <input
              type="text"
              placeholder="Search similar responses..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              style={{
                flex: 1,
                border: 'none',
                backgroundColor: 'transparent',
                color: colors.text,
                fontSize: '14px',
                outline: 'none'
              }}
            />
          </div>
        )}
        </AnimatedCard>
        
        {/* Similar Pairs List */}
        <AnimatedCard delay={80} translateY={12}>
        <div>
          {filteredPairs.map((pair, i) => (
            <SimilarPairCard key={i} pair={pair} index={i} />
          ))}
          {similarPairs.length === 0 && (
            <div className="nsx-metric-card" style={{ 
              padding: '40px', 
              textAlign: 'center', 
              color: colors.textMuted,
              backgroundColor: colors.cardBg,
              borderRadius: '12px',
              border: `1px solid ${colors.border}`
            }}>
              <CheckCircle size={48} style={{ color: colors.success, marginBottom: '12px' }} />
              <div style={{ fontSize: '14px', fontWeight: '600', color: colors.text, marginBottom: '8px' }}>
                No Suspicious Pairs Found
              </div>
              <div>Response diversity looks healthy for this variable.</div>
            </div>
          )}
        </div>
        </AnimatedCard>
      </div>
    );
  };
  
  const renderOutliersTab = () => {
    if (!currentVarData) return null;
    
    const outliers = currentVarData.outliers || [];
    
    return (
      <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0, 1fr)', gap: '16px', marginTop: '80px', width: '100%' }}>
        <VariableSelector />
        
        <MetricCard
          colors={colors}
          isDarkMode={isDarkMode}
          onViewIssues={() => setActiveTab('issues')}
          animDelay={0}
          icon={AlertTriangle}
          title="Outlier Responses"
          value={outliers.length}
          subtitle="Responses that don't fit typical patterns"
          color={outliers.length > 10 ? colors.warning : colors.success}
        />
        
        <AnimatedCard delay={0} translateY={12}>
        <div className="nsx-metric-card" style={{
          backgroundColor: colors.cardBg,
          borderRadius: '12px',
          border: `1px solid ${colors.border}`,
          overflow: 'hidden'
        }}>
          <div style={{
            padding: '12px 16px',
            borderBottom: `1px solid ${colors.border}`,
            backgroundColor: isDarkMode ? '#1F1F23' : '#F9FAFB'
          }}>
            <h4 style={{ fontSize: '13px', fontWeight: '600', color: colors.text, margin: 0 }}>
              Outlier Responses ({outliers.length})
            </h4>
          </div>
          
          {outliers.length > 0 ? (
            <div style={{ maxHeight: '500px', overflowY: 'auto' }}>
              {outliers.map((outlier, i) => (
                <div key={i} style={{
                  padding: '12px 16px',
                  borderBottom: `1px solid ${colors.border}`,
                  display: 'flex',
                  gap: '16px'
                }}>
                  <div style={{
                    width: '32px',
                    height: '32px',
                    borderRadius: '8px',
                    backgroundColor: `${colors.warning}20`,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    flexShrink: 0
                  }}>
                    <AlertTriangle size={16} style={{ color: colors.warning }} />
                  </div>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontSize: '14px', color: colors.text, lineHeight: '1.5', marginBottom: '8px' }}>
                      "{outlier.text}"
                    </div>
                    <div style={{ display: 'flex', gap: '16px', fontSize: '12px', color: colors.textMuted }}>
                      <span>Row: {outlier.row_index}</span>
                      {outlier.collector && <span>Collector: {outlier.collector}</span>}
                      <span>Distance: {outlier.distance_score}</span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div style={{ 
              padding: '40px', 
              textAlign: 'center', 
              color: colors.textMuted 
            }}>
              No outlier responses detected.
            </div>
          )}
        </div>
        </AnimatedCard>
      </div>
    );
  };
  
  const renderCollectorsTab = () => {
    const collectors = byCollector.collectors || {};
    const lowDiversity = byCollector.low_diversity_collectors || [];
    
    return (
      <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0, 1fr)', gap: '16px', marginTop: '80px', width: '100%' }}>
        <div style={{ display: 'grid', gridTemplateColumns: isMobile ? 'repeat(2, minmax(0, 1fr))' : 'repeat(auto-fit, minmax(min(200px, 100%), 1fr))', gap: '16px' }}>
          <MetricCard
            colors={colors}
            isDarkMode={isDarkMode}
            onViewIssues={() => setActiveTab('issues')}
            animDelay={0}
            icon={Users}
            title="Collectors Analyzed"
            value={byCollector.collector_count || 0}
            subtitle="With open-ended responses"
            color={colors.neutral}
          />
          <MetricCard
            colors={colors}
            isDarkMode={isDarkMode}
            onViewIssues={() => setActiveTab('issues')}
            animDelay={80}
            icon={AlertTriangle}
            title="Low Diversity"
            value={lowDiversity.length}
            subtitle="Collectors with repetitive responses"
            color={lowDiversity.length > 0 ? colors.warning : colors.success}
          />
        </div>

        {/* Low Diversity Warning */}
        <AnimatedCard delay={80} translateY={12}>
        {lowDiversity.length > 0 && (
          <div style={{
            padding: '20px 24px',
            backgroundColor: isDarkMode ? '#1C1917' : '#FFFBEB',
            borderRadius: '12px',
            border: `1px solid ${colors.warning}40`
          }}>
            <div style={{ display: 'flex', alignItems: 'flex-start', gap: '12px' }}>
              <AlertTriangle size={20} style={{ color: colors.warning, marginTop: '2px' }} />
              <div>
                <div style={{ fontSize: '13px', fontWeight: '600', color: colors.text, marginBottom: '8px' }}>
                  Low Diversity Collectors Detected
                </div>
                <div style={{ fontSize: '13px', color: colors.textSecondary, marginBottom: '12px' }}>
                  The following collectors have unusually low response diversity ({"<"}50% unique responses):
                </div>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
                  {lowDiversity.map((c, i) => (
                    <span key={i} style={{
                      padding: '6px 12px',
                      backgroundColor: `${colors.warning}20`,
                      color: colors.warning,
                      borderRadius: '16px',
                      fontSize: '13px',
                      fontWeight: '500'
                    }}>
                      {c.collector_id} ({(c.overall_diversity * 100).toFixed(0)}% diversity)
                    </span>
                  ))}
                </div>
              </div>
            </div>
          </div>
        )}
        </AnimatedCard>
        
        {/* Section header + heatmap / table toggle (same pattern as the other detectors) */}
        <AnimatedCard delay={160} translateY={12}>
        <div style={{ display: 'flex', alignItems: 'flex-end', justifyContent: 'space-between', gap: '12px', flexWrap: 'wrap', paddingBottom: '8px', borderBottom: `0.5px solid ${isDarkMode ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.08)'}` }}>
          <div>
            <div style={{ fontSize: '10px', fontWeight: 500, letterSpacing: '0.16em', textTransform: 'lowercase', color: colors.textMuted, marginBottom: '3px' }}>collector analysis</div>
            <div style={{ fontSize: '13.5px', fontWeight: 500, letterSpacing: '-0.01em', color: colors.text }}>Response diversity by data collector</div>
          </div>
          <div style={{ display: 'inline-flex', gap: '2px', padding: '3px', borderRadius: '8px', background: isDarkMode ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.05)' }}>
            {[{ id: 'heatmap', label: 'heatmap' }, { id: 'table', label: 'table' }].map(v => {
              const active = collectorView === v.id;
              return (
                <button key={v.id} onClick={() => setCollectorView(v.id)} style={{
                  padding: '5px 12px', borderRadius: '6px', border: 'none', cursor: 'pointer',
                  fontSize: '12px', fontWeight: 500, letterSpacing: '0.02em',
                  fontFamily: "'Outfit', -apple-system, sans-serif",
                  background: active ? (isDarkMode ? 'rgba(255,255,255,0.12)' : '#FFFFFF') : 'transparent',
                  color: active ? colors.text : colors.textMuted,
                  boxShadow: active ? '0 1px 2px rgba(0,0,0,0.08)' : 'none',
                }}>{v.label}</button>
              );
            })}
          </div>
        </div>
        </AnimatedCard>

        {/* Heatmap view */}
        {collectorView === 'heatmap' && (
          <AnimatedCard delay={240} translateY={12}>
          <div className="nsx-metric-card" style={{
            padding: '18px',
            backgroundColor: isDarkMode ? 'rgba(255,255,255,0.03)' : 'rgba(255,255,255,0.6)',
            borderRadius: '8px',
            border: `0.5px solid ${isDarkMode ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.08)'}`,
            backdropFilter: 'blur(20px)',
            WebkitBackdropFilter: 'blur(20px)',
            overflowX: 'auto',
          }}>
            <div style={{ fontSize: '12px', color: colors.textMuted, marginBottom: '6px', lineHeight: 1.45 }}>
              Share of each collector's answers that are unique. Green means varied answers; amber and red mean
              the same wording keeps coming back, which points to templated or copy-pasted responses.
            </div>
            <TextDiversityHeatmap
              collectors={collectors}
              variables={sortedAnalyzedVariables}
              threshold={0.5}
              isDarkMode={isDarkMode}
            />
          </div>
          </AnimatedCard>
        )}

        {/* Collector Table */}
        {collectorView === 'table' && (
        <AnimatedCard delay={240} translateY={12}>
        <div className="nsx-metric-card" style={{
          backgroundColor: colors.cardBg,
          borderRadius: '12px',
          border: `1px solid ${colors.border}`,
          overflow: 'hidden'
        }}>
          <div style={{ overflowX: 'auto', WebkitOverflowScrolling: 'touch' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: isMobile ? '500px' : 'auto' }}>
            <thead>
              <tr style={{ backgroundColor: isDarkMode ? '#1F1F23' : '#F9FAFB' }}>
                <th style={{ padding: '14px 16px', textAlign: 'left', fontSize: '13px', fontWeight: '600', color: colors.textSecondary }}>
                  Collector
                </th>
                <th style={{ padding: '14px 16px', textAlign: 'center', fontSize: '13px', fontWeight: '600', color: colors.textSecondary }}>
                  Responses
                </th>
                <th style={{ padding: '14px 16px', textAlign: 'center', fontSize: '13px', fontWeight: '600', color: colors.textSecondary }}>
                  Diversity Score
                </th>
                <th style={{ padding: '14px 16px', textAlign: 'center', fontSize: '13px', fontWeight: '600', color: colors.textSecondary }}>
                  Status
                </th>
              </tr>
            </thead>
            <tbody>
              {Object.values(collectors).map((collector, i) => {
                // `diversity_measured` is True only when at least one variable
                // produced valid responses for this collector. Older cached
                // results (pre-fix) won't have the field — fall back to true
                // so we don't retroactively mark every old collector as
                // unmeasured. Run the check fresh to get the new behavior.
                const wasMeasured = collector.diversity_measured ?? true;
                const diversityScore = (collector.overall_diversity || 0) * 100;
                const isLow = wasMeasured && diversityScore < 50;

                return (
                  <tr key={i} style={{ borderTop: `1px solid ${colors.border}` }}>
                    <td style={{ padding: '14px 16px', fontSize: '14px', color: colors.text, fontWeight: '500' }}>
                      {collector.collector_id}
                    </td>
                    <td style={{ padding: '14px 16px', textAlign: 'center', fontSize: '14px', color: colors.text }}>
                      {collector.total_responses}
                    </td>
                    <td style={{ padding: '14px 16px', textAlign: 'center' }}>
                      {!wasMeasured ? (
                        <span style={{ fontSize: '13px', color: colors.textMuted, fontStyle: 'italic' }}>
                          — no text data
                        </span>
                      ) : (
                        <div style={{
                          display: 'inline-flex',
                          alignItems: 'center',
                          gap: '8px'
                        }}>
                          <div style={{
                            width: '60px',
                            height: '6px',
                            backgroundColor: isDarkMode ? '#2D2E32' : '#E5E7EB',
                            borderRadius: '3px',
                            overflow: 'hidden'
                          }}>
                            <div style={{
                              width: `${diversityScore}%`,
                              height: '100%',
                              backgroundColor: isLow ? colors.warning : colors.success,
                              borderRadius: '3px'
                            }} />
                          </div>
                          <span style={{
                            fontSize: '13px',
                            fontWeight: '600',
                            color: isLow ? colors.warning : colors.success
                          }}>
                            {diversityScore.toFixed(0)}%
                          </span>
                        </div>
                      )}
                    </td>
                    <td style={{ padding: '14px 16px', textAlign: 'center' }}>
                      {!wasMeasured ? (
                        <span style={{
                          padding: '4px 10px',
                          backgroundColor: `${colors.textMuted}15`,
                          color: colors.textMuted,
                          borderRadius: '12px',
                          fontSize: '12px',
                          fontWeight: '500'
                        }}>
                          —
                        </span>
                      ) : isLow ? (
                        <span style={{
                          padding: '4px 10px',
                          backgroundColor: `${colors.warning}15`,
                          color: colors.warning,
                          borderRadius: '12px',
                          fontSize: '12px',
                          fontWeight: '500'
                        }}>
                          Review
                        </span>
                      ) : (
                        <span style={{
                          padding: '4px 10px',
                          backgroundColor: `${colors.success}15`,
                          color: colors.success,
                          borderRadius: '12px',
                          fontSize: '12px',
                          fontWeight: '500'
                        }}>
                          Good
                        </span>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
          </div>
        </div>
        </AnimatedCard>
        )}
      </div>
    );
  };
  
  // ============== MAIN RENDER ==============
  
  if (!data || !data.overall) {
    return (
      <div style={{
        padding: '60px',
        textAlign: 'center',
        color: colors.textMuted
      }}>
        <MessageSquare size={48} style={{ marginBottom: '12px', opacity: 0.5 }} />
        <div style={{ fontSize: '14px', fontWeight: '600', color: colors.text, marginBottom: '8px' }}>
          No Text Analysis Data
        </div>
        <div>Run the Text Analysis check to see clustering results here.</div>
      </div>
    );
  }
  
  return (
    <div 
      className="text-analysis-section activeQualityDetector"
      style={{
        width: '100%',
        maxWidth: '100%',
        padding: isMobile ? '0 8px 24px 8px' : '0 45px 24px 45px',
        boxSizing: 'border-box',
        overflow: isMobile ? 'visible' : 'hidden',
      }}
    >
      <DimensionHeader
        slug="text analysis"
        title="Text Analysis"
        description="Groups similar open-ended responses and flags copy-paste, gibberish, or off-topic answers. Helps spot enumerator templates, bot submissions, and low-effort responses."
        rememberCollapseKey="text_analysis"
        tabs={tabs.map(t => {
          if (t.id === 'clusters')   return { ...t, count: overallStats.total_clusters_found || 0, countTone: 'neutral' };
          if (t.id === 'similar')    return { ...t, count: allSimilarPairs.length,
                                              countTone: allSimilarPairs.length > 5 ? 'alert' : 'neutral' };
          if (t.id === 'outliers')   return { ...t, count: allGibberishIssues.length,
                                              countTone: allGibberishIssues.length > 0 ? 'alert' : 'neutral' };
          if (t.id === 'collectors') return { ...t, count: Object.keys(byCollector).length, countTone: 'neutral' };
          if (t.id === 'issues')     return { ...t, count: allSimilarPairs.length + allGibberishIssues.length,
                                              countTone: (allSimilarPairs.length + allGibberishIssues.length) > 0 ? 'alert' : 'neutral' };
          return t;
        })}
        activeTab={activeTab}
        onTabChange={setActiveTab}
        isMobile={isMobile}
        isSidePanelExpanded={isSidePanelExpanded}
      />
      <div style={{ minHeight: '600px', marginTop: '16px' }}>
        {activeTab === 'overview' && renderOverviewTab()}
        {activeTab === 'clusters' && renderClustersTab()}
        {activeTab === 'similar' && renderSimilarTab()}
        {activeTab === 'outliers' && renderOutliersTab()}
        {activeTab === 'collectors' && renderCollectorsTab()}

        {activeTab === 'issues' && (
          <AnimatedCard delay={0} translateY={12}>
          <IssueLogPanel
            issueGroups={[
              ...(allSimilarPairs.length > 0 ? [{
                issues: allSimilarPairs,
                title: 'Similar Responses',
                issueType: 'similar',
                totalCount: allSimilarPairs.length
              }] : []),
              ...(allGibberishIssues.length > 0 ? [{
                issues: allGibberishIssues,
                title: 'Gibberish Responses',
                issueType: 'gibberish',
                totalCount: totalGibberishCount
              }] : [])
            ]}
          />
          </AnimatedCard>
        )}
      </div>
    </div>
  );
};

export default TextAnalysisSection;